// The Engineering lock step's tab (docs/job-view-brief.md section 1, "Two locks, named apart": the sold revision froze at
// Won; the Engineering lock freezes Rev 0 for purchasing and build, and "Lock as Rev 0" is reached from this step, not a
// loose button on Details). The Lock and revisions panel as it was on Details: the state line, the revisions with their
// change lists, Lock as Rev N with its confirm, and the open change order's note. The Engineering gate repeats here: the
// lock waits while anything on the job is unverified (the list is on Details). A lock keeps the base build's whole
// resolution as the numbered revision.

import { STEP, engineeringBlockers } from '@zerogap/engine';
import { insertRow, updateRow, useRows } from '../../lib/api';
import { longDate } from '../../lib/format';
import { baseBuild, pricedUnder } from '../../lib/job';
import { useSession } from '../../lib/session';
import { Btn, Empty, Info, Ln, Money, Panel } from '../../ui/kit';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-details.css';

export function Lock({ ctx }: { ctx: JobCtx }) {
  const { data, jobId } = ctx;
  const s = useSession();
  const job = data.job!;
  // the revision stores the base: the build the proposal leads with (CLAUDE.md, Builds: "a locked revision stores it")
  const res = pricedUnder(ctx.asEntered, baseBuild(data.builds), []) ?? ctx.r.res!;
  const revisions = useRows('revision', { eq: { job_id: jobId }, order: ['rev_no'] });
  const resolutions = useRows('resolution', { eq: { job_id: jobId } });
  const users = useRows('user');
  const changeOrders = useRows('change_order', { eq: { job_id: jobId } });
  const scopeRows = useRows('scope_row', { eq: { opportunity_id: job.opportunity_id }, enabled: !!job.opportunity_id });
  const openChange = changeOrders.rows.find((x) => x.state === 'draft' || x.state === 'sent');
  const canEdit = s.canEdit('screen.jobs');
  const blockers = engineeringBlockers(res, { job: job as any, items: data.items as any[], scopeRows: scopeRows.rows as any[] });
  const won = ctx.steps.find((x) => x.key === STEP.won);

  const lock = async () => {
    const revNo = revisions.rows.length;
    const prev = revisions.rows.at(-1);
    const prevTotals = prev ? resolutions.rows.find((x) => x.id === prev.resolution_id)?.totals : null;
    const changes: string[] = [];
    if (prevTotals) {
      const d = res.totals.price_cents - Number(prevTotals.price_cents ?? 0);
      changes.push(`Price ${d === 0 ? 'unchanged' : `${d > 0 ? 'up' : 'down'} $${(Math.abs(d) / 100).toFixed(2)}`}`);
      const h = Number(res.totals.labor_hours) - Number(prevTotals.labor_hours ?? 0);
      changes.push(`Hours ${Math.abs(h) < 0.005 ? 'unchanged' : `${h > 0 ? 'up' : 'down'} ${Math.abs(h).toFixed(2)}`}`);
    }
    if (!window.confirm(`Lock this job as Rev ${revNo}? Its numbers stop moving with the standards until a revision is started.`)) return;
    const resolution = await insertRow('resolution', { job_id: jobId, standards_version_id: res.standards_version_id, items: res.items, totals: res.totals, chain: res.groups });
    const revision = await insertRow('revision', { job_id: jobId, rev_no: revNo, resolution_id: resolution.id, change_list: changes, locked_by: s.user?.id ?? null });
    // the sheets in force now belong to this revision; a sheet uploaded later belongs to the next
    for (const v of data.views) if (!v.revision_id) await updateRow('view', v.id, { revision_id: revision.id });
    await updateRow('job', jobId, { locked_at: new Date().toISOString(), standards_version_id: res.standards_version_id });
  };

  return (
    <div className="jvl">
      <p className="lead">
        <b>Rev 0 for purchasing and build; the sold revision froze at {won?.name ?? 'Won'}.</b>
      </p>
      <Panel label="Lock and revisions" right={<Info left>Numbers stay live through estimating and proposal, and freeze at engineering lock. A locked job keeps the standards version it was priced on.</Info>}>
        <Ln left="State" right={job.locked_at ? `Locked ${longDate(job.locked_at)} · standards ${res.standards_version_name}` : `Live · re-resolves at current standards (${res.standards_version_name})`} />
        {revisions.rows.length === 0 && <Empty>No revision has been locked yet.</Empty>}
        {revisions.rows.map((rev) => {
          const totals = resolutions.rows.find((x) => x.id === rev.resolution_id)?.totals;
          return (
            <Ln
              key={rev.id}
              left={
                <>
                  <b>Rev {rev.rev_no}</b> · {longDate(rev.locked_at)} · {users.rows.find((u) => u.id === rev.locked_by)?.name ?? '—'}
                  {Array.isArray(rev.change_list) && rev.change_list.length > 0 && (
                    <div>
                      <small>{rev.change_list.join(' · ')}</small>
                    </div>
                  )}
                </>
              }
              right={<Money cents={totals?.price_cents} />}
            />
          );
        })}
        {canEdit && s.can('money') && (
          <div className="row mid" style={{ marginTop: 10, gap: 6, flexWrap: 'wrap' }}>
            {job.locked_at ? (
              <>
                <small>A locked job changes through a change order (Details).</small>
                <Btn onClick={() => window.confirm('Re-price this job at current standards? The number may legitimately change.') && void updateRow('job', jobId, { locked_at: null, standards_version_id: null })}>
                  Re-price at current standards
                </Btn>
              </>
            ) : openChange ? (
              <small>Change order {openChange.number} is open: approving it locks the job as Rev {revisions.rows.length}.</small>
            ) : blockers.length > 0 ? (
              <div className="gate">
                <Btn primary disabled title="The Engineering gate: verify everything on the job first">
                  Lock as Rev {revisions.rows.length}
                </Btn>
                <span>
                  {blockers.length} unverified on this job: the lock waits until each is verified.{' '}
                  <button type="button" className="linklike" onClick={() => ctx.go({ tab: 'details', lit: null, back: null })}>
                    The Engineering list is on Details
                  </button>
                </span>
              </div>
            ) : (
              <Btn primary onClick={() => void lock()}>
                Lock as Rev {revisions.rows.length}
              </Btn>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
